import * as React from "react";
import { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { Feature } from "ol";
import { IInfo } from "../Popups/Info/Info";
import { IToast } from "../Elements/Toast";
import { IBookmark } from "../Map/Bookmarks/BookmarksWidget";
import { getUrlParam } from "../Map/utils";
import { getCurrentActiveToken } from "../Map/MapDownload/Token";

export interface INomaContext {
    hasMap: boolean;
    setHasMap: Dispatch<SetStateAction<boolean>>;
    language: string;
    setLanguage: Dispatch<SetStateAction<string>>;
    isMobile: boolean;
    isIFrame: boolean;
    locateFeature: IFeatureTimeStamp;
    setLocateFeature: (feature: Feature) => void;
    highlightFeatures: IFeatureArrayTimeStamp;
    setHighlightFeatures: Dispatch<SetStateAction<Feature[]>>;
    enabledLayers: string[];
    setEnabledLayers: Dispatch<SetStateAction<string[]>>;
    info: IInfo;
    setInfo: Dispatch<SetStateAction<IInfo>>;
    mapClick: any;
    setMapClick: Dispatch<SetStateAction<any>>;
    showToast: IToast;
    setShowToast: Dispatch<SetStateAction<IToast>>;
    bookmarks: IBookmark[];
    setBookmarks: Dispatch<SetStateAction<IBookmark[]>>;
    selectedBookmarkId: number;
    setSelectedBookmarkId: Dispatch<SetStateAction<number>>;
    activeToken: string;
    setActiveToken: Dispatch<SetStateAction<string>>;
}

export interface IApplyFeature {
    feature: Feature;
    isRight?: boolean;
}
export interface IFeatureTimeStamp {
    feature: Feature;
    timestamp: number;
}
export interface IFeatureArrayTimeStamp {
    features: Feature[];
    timestamp: number;
}

const storageName = "GeoLiteBookmarks";

const urlLanguage = getUrlParam("lang");
const defaultLanguage = /* localStorage.getItem("GeoLiteLanguage") ||*/ urlLanguage || /* configA?.language || */ "LV";

const NomaContext = createContext<INomaContext>(null);

export const useAppContext = (): INomaContext => useContext(NomaContext);

export const AppContextProvider: FC = (props: React.PropsWithChildren<INomaContext>) => {
    const [hasMap, setHasMap] = useState<boolean>(true);
    const [language, setLanguage] = useState<string>(defaultLanguage.toUpperCase());
    const [locateFeature, setLocateFeature] = useState<IFeatureTimeStamp>(null);
    const [highlightFeatures, setHighlightFeatures] = useState<IFeatureArrayTimeStamp>(null);
    const [enabledLayers, setEnabledLayers] = useState<string[]>([]);
    const [info, setInfo] = useState<IInfo>(null);
    const [mapClick, setMapClick] = useState<any>();
    const [showToast, setShowToast] = useState<IToast>();
    const [bookmarks, setBookmarks] = useState<IBookmark[]>(getStoredBookmarks());
    const [selectedBookmarkId, setSelectedBookmarkId] = useState<number>();
    const [activeToken, setActiveToken] = useState<string>(getCurrentActiveToken());

    const isMobile =
        navigator.maxTouchPoints > 0 || navigator.platform === "iPhone" || navigator.platform === "iPad" || iniFrame();

    const isIFrame = iniFrame();

    useEffect(() => {
        bookmarks.length && localStorage.setItem(storageName, JSON.stringify(bookmarks));
    }, [bookmarks]);

    const prepareLocateFeature = useCallback((feature) => {
        setLocateFeature({ feature, timestamp: Date.now() });
    }, []);

    const prepareHighlightFeatures = useCallback((features) => {
        setHighlightFeatures({ features, timestamp: Date.now() });
    }, []);

    const prepareLanguage = useCallback(
        (lang: string) => {
            if (lang !== language) {
                setLanguage(lang);
                // TODO save on localstorage
                // localStorage.setItem("GeoLiteLanguage", lang);
            }
        },
        [language]
    );

    const values = {
        hasMap,
        setHasMap,
        language,
        setLanguage: prepareLanguage,
        isMobile,
        isIFrame,
        locateFeature,
        setLocateFeature: prepareLocateFeature,
        highlightFeatures,
        setHighlightFeatures: prepareHighlightFeatures,
        enabledLayers,
        setEnabledLayers,
        info,
        setInfo,
        mapClick,
        setMapClick,
        showToast,
        setShowToast,
        bookmarks,
        setBookmarks,
        selectedBookmarkId,
        setSelectedBookmarkId,
        activeToken,
        setActiveToken
    };

    return <NomaContext.Provider value={values}>{props.children}</NomaContext.Provider>;
};

const iniFrame = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

const getTimeFormated = (value: number) => {
    const date = new Date(value);
    return date.getUTCHours().toString().padStart(2, "0") + ":" + date.getUTCMinutes().toString().padStart(2, "0");
};

const getStoredBookmarks = () => {
    const storedBookmarks = localStorage.getItem(storageName);
    let parsedBookmarks: [];
    try {
        parsedBookmarks = storedBookmarks ? JSON.parse(storedBookmarks) : [];
    } catch (e) {
        parsedBookmarks = [];
    }
    return parsedBookmarks;
};
